import { FrontierService } from '@api';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  syslogActionsDocument,
  syslogActorsDocument,
  syslogSourcesDocument,
  syslogTargetTypesDocument,
  syslogTargetsDocument,
} from './gql';
import { SystemLogKeys } from './queryKeys';

const ITEMS_PER_PAGE = 50;

export function useGetSyslogFilterOptions(enabled = true) {
  const actions = useQuery({
    queryKey: [SystemLogKeys.actions],
    queryFn: async () => {
      const res = await FrontierService.request(syslogActionsDocument);
      return res.syslogFilterOptions.actions;
    },
    enabled,
  });

  const sources = useQuery({
    queryKey: [SystemLogKeys.sources],
    queryFn: async () => {
      const res = await FrontierService.request(syslogSourcesDocument);
      return res.syslogFilterOptions.sources;
    },
    enabled,
  });

  const targetTypes = useQuery({
    queryKey: [SystemLogKeys.targetTypes],
    queryFn: async () => {
      const res = await FrontierService.request(syslogTargetTypesDocument);
      return res.syslogFilterOptions.targetTypes;
    },
    enabled,
  });

  return {
    actions: actions.data || [],
    sources: sources.data || [],
    targetTypes: targetTypes.data || [],
    isLoading: actions.isLoading || sources.isLoading || targetTypes.isLoading,
    isFetched: actions.isFetched && sources.isFetched && targetTypes.isFetched,
  };
}

interface GetPaginatedOptions {
  search?: string;
}

export function useGetSyslogActors({ search }: GetPaginatedOptions) {
  return useInfiniteQuery({
    queryKey: [SystemLogKeys.actors, search],
    queryFn: async ({ pageParam = 0 }) => {
      const res = await FrontierService.request(syslogActorsDocument, {
        pagination: {
          offset: pageParam * ITEMS_PER_PAGE,
          take: ITEMS_PER_PAGE,
        },
        search,
      });
      return res.syslogFilterOptions.actors || { hasMore: false, data: [] };
    },
    getNextPageParam: (lastPage, pages) => (lastPage?.hasMore ? pages.length : undefined),
    keepPreviousData: true,
  });
}

export function useGetSyslogTargets({ search }: GetPaginatedOptions) {
  return useInfiniteQuery({
    queryKey: [SystemLogKeys.targets, search],
    queryFn: async ({ pageParam = 0 }) => {
      const res = await FrontierService.request(syslogTargetsDocument, {
        pagination: {
          offset: pageParam * ITEMS_PER_PAGE,
          take: ITEMS_PER_PAGE,
        },
        search,
      });
      return res.syslogFilterOptions.targets || { hasMore: false, data: [] };
    },
    getNextPageParam: (lastPage, pages) => (lastPage?.hasMore ? pages.length : undefined),
    keepPreviousData: true,
  });
}
